import { SearchModel } from '@internal/blocks/SearchResultsBlock/TSearchParams'
import {
  aboveMaxSearchResultsLabel,
  maxSearchResults,
} from '@internal/blocks/SearchResultsBlock/maxSearchResults'
import { TPopularBrandsModelsForFromMachineCategoryPages } from '@internal/services/cmsClient'
import { MachineType } from '@internal/utils/machine/MachineType'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { useModelLabel } from '@internal/utils/machine/useModelLabel'
import { tracker } from '@internal/utils/tracking/tracker'
import { Typography } from '@renderer-ui-library/mui'
import { BlocksContent, BlocksRenderer } from '@strapi/blocks-react-renderer'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { memo, useCallback, useMemo } from 'react'
import { useMachineTypeName } from 'utils/machine/useMachineTypeName'
import styles from './searchResultsTitle.module.scss'

export interface SearchResultsTitleProps {
  brands: string[]
  resultsCount: number
  machineType: MachineType | undefined
  popularBrands?: TPopularBrandsModelsForFromMachineCategoryPages[]
  popularModels?: TPopularBrandsModelsForFromMachineCategoryPages[]
  seoDescription?: BlocksContent | null
  selectedModels: SearchModel[]
  hasLoadedOnce: boolean
  hasAnySearchParams: boolean
}
export const SearchResultsTitle: React.FC<SearchResultsTitleProps> = memo(
  ({
    brands,
    selectedModels,
    resultsCount,
    machineType,
    popularBrands,
    popularModels,
    seoDescription,
    hasLoadedOnce,
    hasAnySearchParams,
  }) => {
    const { t } = useTranslation(localeNamespace.common)
    const { getMachineTypeName } = useMachineTypeName()
    const { getModelLabel } = useModelLabel()

    const title = useMemo(() => {
      if (!hasAnySearchParams) {
        return t(translations.serpNoParamsTitle)
      }

      let category: string | null = null
      const modelsString = selectedModels
        .map((m) => getModelLabel(m.name, m.type))
        .join(', ')
      if (brands.length > 0) {
        const joinedBrands = brands.join(', ')

        if (selectedModels.length) {
          category = `${joinedBrands} ${modelsString}`.trim()
        } else if (machineType) {
          category = `${joinedBrands} ${getMachineTypeName(
            machineType,
            resultsCount === 1 ? 'singular' : 'plural'
          )}`
        } else {
          category = joinedBrands
        }
      }

      if (!category) {
        category = `${modelsString} ${
          machineType
            ? getMachineTypeName(
                machineType,
                resultsCount === 1 ? 'singular' : 'plural'
              )
            : ''
        }`.trim()
      }

      const count =
        resultsCount >= maxSearchResults
          ? aboveMaxSearchResultsLabel
          : resultsCount

      return t(translations.searchResultsTitle, {
        category,
        amount: !hasLoadedOnce ? '' : count.toString(),
      })
    }, [
      hasAnySearchParams,
      selectedModels,
      brands,
      resultsCount,
      t,
      hasLoadedOnce,
      getModelLabel,
      machineType,
      getMachineTypeName,
    ])

    const router = useRouter()

    const handlePopularLinkClick = useCallback(
      (ev: React.SyntheticEvent<HTMLAnchorElement>) => {
        ev.preventDefault()
        tracker.trackEvent({
          name: 'click',
          target_name: 'machine_category_page_popular_link.serp',
          is_interaction: true,
        }),
          router.push(ev.currentTarget.href)
      },
      [router]
    )

    const modelLinks = useMemo(
      () =>
        (brands.length > 0 &&
          popularModels &&
          popularModels.filter((model) =>
            brands.some(
              (brand) => model.brand.toLowerCase() === brand.toLowerCase()
            )
          )) ||
        [],
      [brands, popularModels]
    )

    const showPopularBrands =
      brands.length === 0 && popularBrands && popularBrands.length > 0
    const showPopularModels = modelLinks.length > 0

    return (
      <div className={styles.wrapper}>
        <div>
          <Typography variant={hasAnySearchParams ? 'h3' : 'h4'} component='h1'>
            {title}
          </Typography>

          {seoDescription && (
            <div className={styles.seoDescription}>
              <BlocksRenderer
                content={seoDescription}
                blocks={{
                  paragraph: ({ children }) => (
                    <Typography variant='body1' color='textSecondary'>
                      {children}
                    </Typography>
                  ),
                  link: ({ children, url }) => (
                    <Link href={url}>{children}</Link>
                  ),
                }}
              />
            </div>
          )}
        </div>
        {(showPopularBrands || showPopularModels) && (
          <div className={styles.popularLinksWrapper}>
            {showPopularBrands && (
              <>
                <Typography variant='caption' color='textSecondary'>
                  <strong>
                    {t(translations.searchResultsPopularBrandsLabel)}:
                  </strong>{' '}
                </Typography>
                {popularBrands.map((brand, index) => (
                  <Link
                    href={brand.route}
                    onClick={handlePopularLinkClick}
                    key={brand.brand}
                  >
                    <Typography variant='caption' color='text.primaryLight'>
                      {index < popularBrands.length - 1
                        ? `${brand.brand},`
                        : brand.brand}
                    </Typography>
                  </Link>
                ))}
              </>
            )}
            {showPopularModels && (
              <>
                <Typography variant='caption' color='textSecondary'>
                  <strong>
                    {t(translations.searchResultsPopularModelsLabel)}:
                  </strong>{' '}
                </Typography>
                {modelLinks.map(({ model, route }, index) => {
                  const isActive = selectedModels.some(
                    (m) =>
                      m.name.toLocaleLowerCase() === model?.toLocaleLowerCase()
                  )

                  return (
                    <Link
                      href={route}
                      onClick={handlePopularLinkClick}
                      key={model}
                    >
                      <Typography
                        variant='caption'
                        color={isActive ? 'secondary' : 'text.primaryLight'}
                      >
                        {index < modelLinks.length - 1 ? `${model},` : model}
                      </Typography>
                    </Link>
                  )
                })}
              </>
            )}
          </div>
        )}
      </div>
    )
  }
)

SearchResultsTitle.displayName = 'SearchResultsTitle'
